import type { GlobalRuleList, GuardrailList } from '@matter_extractor/shared';
import { globalRuleListSchema, guardrailListSchema } from '@matter_extractor/shared';
import { ApiError } from './api';

/**
 * The two READS of the rules and the guardrails as they stand NOW (§8.5,
 * §11.3) - what the conversation of a session names a turn's rules from when
 * the session froze none.
 *
 * Nothing writes through here any more (issue #192, vitrine.md section 5): the
 * rules and the guardrails are the versioned singleton « Les règles », edited
 * through `ruleSettingsApi` of `api.ts` like the texts of the noyaux. These two
 * answer the list of the version under the pointer of the server's side.
 *
 * Its own file rather than another family in `lib/api.ts`, for the reason
 * `lib/links-api.ts` gives: `api.ts` is a long module several lots edit, and two
 * lots editing one of them is a merge that goes wrong in the only place it
 * cannot be seen. `ApiError` is IMPORTED from there rather than re-declared: it
 * is the type every screen catches, and two classes of the same name would make
 * `instanceof` answer false on one of them.
 *
 * Every answer is PARSED before it is returned: a response is an external input
 * even when we wrote the server.
 */

const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

/**
 * The credential travels in a cookie and NEVER in the URL - the line the whole
 * administration holds. Same object as `ADMIN_INIT` of `api.ts`, and `as const
 * satisfies` for the same reason: `credentials` has to stay the literal type.
 */
const ADMIN_INIT = {
  headers: { 'content-type': 'application/json' },
  credentials: 'include',
} as const satisfies RequestInit;

async function request(path: string): Promise<unknown> {
  const response = await fetch(`${API_URL}${path}`, ADMIN_INIT);
  if (!response.ok) {
    let message = 'La requête a échoué.';
    let details: unknown;
    let code: string | null = null;
    try {
      const body = (await response.json()) as {
        error?: string;
        message?: string;
        details?: unknown;
      };
      if (body.message) message = body.message;
      details = body.details ?? body;
      code = body.error ?? null;
    } catch {
      // Non-JSON error body: keep the generic message.
    }
    throw new ApiError(response.status, message, details, code);
  }
  return response.json();
}

/** The whole list, by priority - the first rule is the one that prevails (§8.5). */
export function fetchRules(): Promise<GlobalRuleList> {
  return request('/api/rules').then((body) => globalRuleListSchema.parse(body));
}

/** The whole list, in its reading order. A guardrail has no priority (§8.5). */
export function fetchGuardrails(): Promise<GuardrailList> {
  return request('/api/guardrails').then((body) => guardrailListSchema.parse(body));
}
